import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState([]); // GA properties OR Ads customers
  const [tokens, setTokens] = useState(null);
  const [flow, setFlow] = useState(null); // "ga" | "ads"

  const hasRun = React.useRef(false);

  useEffect(() => {
    if (hasRun.current) return; // prevent double-invocation in React StrictMode (dev)
    hasRun.current = true;

    const exchangeCode = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const state = params.get("state");

      if (!code) {
        return navigate("/channels?error=no-code");
      }

      // Determine which flow we're in – treat googleAds / googleads as Ads
      const isAds = /googleAds/i.test(state || "");
      setFlow(isAds ? "ads" : "ga");

      try {
        const redirectUri = `${window.location.origin}/auth/google/callback`;
        const userId = user?.userId || localStorage.getItem("mb_userId");
        const endpointBase = `${process.env.REACT_APP_API_URL}/auth`;

        if (isAds) {
          const resp = await axios.post(`${endpointBase}/googleads/exchange-code`, {
            code,
            redirectUri,
            userId,
          });
          setTokens(resp.data.tokens);
          setOptions(resp.data.customers || []);
          setLoading(false);
        } else {
          const resp = await axios.post(`${endpointBase}/google/exchange-code`, {
            code,
            redirectUri,
            userId,
          });
          setTokens(resp.data.tokens);
          setOptions(resp.data.properties || []);
          setLoading(false);
        }
      } catch (err) {
        console.error("Token exchange failed:", err);
        setError(err.response?.data?.error || "Token exchange failed");
        setLoading(false);
      }
    };

    exchangeCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const handleSelect = async (selectedId) => {
    if (!tokens) return;
    try {
      setLoading(true);
      const endpointBase = `${process.env.REACT_APP_API_URL}/auth`;
      const userId = user?.userId || localStorage.getItem("mb_userId");

      if (flow === "ads") {
        await axios.post(`${endpointBase}/googleads/complete-auth`, {
          userId,
          customerId: selectedId,
          ...tokens,
        });
        navigate("/channels?connected=gads");
      } else {
        await axios.post(`${endpointBase}/google/complete-auth`, {
          userId,
          propertyId: selectedId,
          ...tokens,
        });
        navigate("/channels?connected=google");
      }
    } catch (err) {
      console.error("Failed to complete auth:", err);
      setError(err.response?.data?.error || "Failed to complete authentication");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-red-500">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="mb-4 text-xl font-bold">
        {flow === "ads"
          ? "Select your Google Ads account"
          : "Select your Google Analytics 4 property"}
      </h2>
      {options.length === 0 ? (
        <p>
          {flow === "ads"
            ? "No Google Ads accounts were found for your user."
            : "No GA4 properties were found for your user."}
        </p>
      ) : (
        <ul className="space-y-2">
          {options.map((opt) => (
            <li key={flow === "ads" ? opt.customerId : opt.propertyId}>
              <button
                className="w-full rounded border px-4 py-2 text-left hover:bg-accent"
                onClick={() =>
                  handleSelect(flow === "ads" ? opt.customerId : opt.propertyId)
                }
              >
                {opt.displayName || opt.customerId || opt.propertyId}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GoogleCallback;
